/**
 * Session Manager Utility
 * Handles session timer, automatic logout, and token expiration
 */

class SessionManager {
  constructor() {
    this.expiryCheckInterval = null;
    this.listeners = [];
    this.warningShown = false;
  }

  /**
   * Initialize session manager with token expiry time
   * @param {number} expiryTime - Unix timestamp in seconds when token expires
   */
  init(expiryTime) {
    // Clear any existing interval
    this.cleanup();
    
    // Store expiry time in localStorage
    localStorage.setItem('tokenExpiry', expiryTime.toString());
    
    // Start checking for expiry
    this.startExpiryCheck();
  }

  /**
   * Start interval to check token expiry
   */
  startExpiryCheck() {
    // Check every 10 seconds
    this.expiryCheckInterval = setInterval(() => {
      this.checkExpiry();
    }, 10000);

    // Also check immediately
    this.checkExpiry();
  }

  /**
   * Check if token has expired or is about to expire
   */
  checkExpiry() {
    const expiryTime = localStorage.getItem('tokenExpiry');
    if (!expiryTime) return;

    const now = Math.floor(Date.now() / 1000);
    const timeLeft = parseInt(expiryTime) - now;

    // Notify all listeners about time update
    this.notifyListeners({ timeLeft, expired: timeLeft <= 0 });

    // If expired, trigger logout
    if (timeLeft <= 0) {
      this.handleExpiry();
    }
  }

  /**
   * Handle token expiry - logout user
   */
  handleExpiry() {
    this.cleanup();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiry');
    
    // Redirect to login
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?session_expired=true';
    }
  }

  /**
   * Get remaining time in seconds
   * @returns {number} Seconds until expiry
   */
  getRemainingTime() {
    const expiryTime = localStorage.getItem('tokenExpiry');
    if (!expiryTime) return 0;

    const now = Math.floor(Date.now() / 1000);
    const timeLeft = parseInt(expiryTime) - now;
    return Math.max(0, timeLeft);
  }

  /**
   * Format remaining time as string (HH:MM:SS or MM:SS)
   * @returns {string} Formatted time
   */
  getFormattedTime() {
    const seconds = this.getRemainingTime();
    
    if (seconds <= 0) return '00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Subscribe to session updates
   * @param {Function} callback - Called with { timeLeft, expired }
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.push(callback);
    
    // Immediately notify with current state
    const timeLeft = this.getRemainingTime();
    callback({ timeLeft, expired: timeLeft <= 0 });

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(data) {
    this.listeners.forEach(callback => callback(data));
  }

  /**
   * Check if session is initialized
   */
  isInitialized() {
    return !!localStorage.getItem('tokenExpiry');
  }

  /**
   * Cleanup intervals and listeners
   */
  cleanup() {
    if (this.expiryCheckInterval) {
      clearInterval(this.expiryCheckInterval);
      this.expiryCheckInterval = null;
    }
    this.listeners = [];
    this.warningShown = false;
  }

  /**
   * Manually trigger logout
   */
  logout() {
    this.handleExpiry();
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
